<template>
  <!-- PBI 11217: read-only feedback view of a conversation, from its start through the last message with feedback
       (the server truncates). Same popup and chat setup as ChatOverview, with its own state; .chat-container keeps
       the chat's message box hidden (_chat-overview.scss), so nothing can be typed or sent here. -->
  <div class="chat-popup">
    <DxPopup
      width="80vw"
      height="90vh"
      v-model:visible="isFeedbackOverviewVisible"
      :hide-on-outside-click="false"
      title="Conversation feedback"
      :show-title="true"
      :show-close-button="false"
    >
      <DxToolbarItem
        widget="dxButton"
        :options="buttonOptions"
        location="after"
      />
      <template #content>
        <div class="chat-container">
          <DxChat
            :items="messages"
            :user="currentUser"
            :showAvatar="true"
            :showUserName="true"
            :showDayHeaders="true"
            :showMessageTimestamp="true"
            :dayHeaderFormat="'dd/MM/yyyy'"
            :messageTimestampFormat="'hh:mm a'"
            message-template="message"
            class="chat-content"
            empty-view-template="empty-view-template"
            ><template #message="{ data }">
              <div
                class="chat-message-text"
                v-html="renderMessage(data.message)"
              ></div>
              <MessageFeedbackSummary
                v-if="hasFeedback(data.message)"
                :message-id="data.message.id"
                :rating="data.message.rating"
                :thumbs-down-reason="data.message.thumbsDownReason"
                :comment="data.message.comment"
                :reason-options="thumbsDownReasonOptions"
              />
            </template>
            <template #empty-view-template="{ data }">
              <div>{{ data.texts.message }}</div>
            </template>
          </DxChat>
        </div>
      </template>
    </DxPopup>
  </div>
</template>
<script setup>
import { computed } from "vue";
import store from "../../../store/index";
import MarkdownIt from "markdown-it";
import { DxPopup, DxToolbarItem } from "devextreme-vue/popup";
import { DxChat } from "devextreme-vue/chat";
import MessageFeedbackSummary from "./MessageFeedbackSummary.vue";
import EMessageRating from "../../../enums/EMessageRating";

// Escaped rendering for this view: raw HTML in a message is shown as text (html: false). ChatOverview is unchanged.
const md = new MarkdownIt({
  html: false,
  linkify: true,
  breaks: true
});

const closeFeedbackOverview = () =>
  store.commit("aiAssistantLogs/closeFeedbackOverviewPopup");

const buttonOptions = {
  icon: "close",
  hint: "Close",
  onClick: closeFeedbackOverview
};

const renderMessage = message => {
  if (!message || !message.text) return "";

  return md.render(message.text);
};

// Same rule as the server's LogsFeedbackView.HasFeedback: the messages that get the read-only feedback view
// (thumbsDownReason is a flags value; 0 = no reason).
const hasFeedback = message =>
  (message.rating ?? EMessageRating.None) !== EMessageRating.None ||
  (message.thumbsDownReason ?? 0) !== 0 ||
  !!message.comment?.trim();

const messages = computed(
  () => store.state.aiAssistantLogs.feedbackMessagesFromSelectedConversation
);
const thumbsDownReasonOptions = computed(
  () => store.state.aiAssistantLogs.thumbsDownReasonOptions ?? []
);
const currentUser = computed(() => store.state.aiAssistantLogs.currentUser);

// The setter lets DevExtreme's own hide (e.g. Escape) close the popup in the store too.
const isFeedbackOverviewVisible = computed({
  get: () => store.state.aiAssistantLogs.isFeedbackOverviewVisible,
  set: visible => {
    if (!visible) closeFeedbackOverview();
  }
});
</script>
